import {
  LocalStorageContextType,
  LocalStorageProviderPropertiesType
} from "@website/hooks/local-storage/local-storage.types";
import { isSSR } from "@website/utils/is-ssr";
import { FC, createContext, useContext, useMemo } from "react";

export const LocalStorageContext = createContext<LocalStorageContextType>({
  addToLocalStorage: (() => null) as (key: string, value: string) => null,
  removeFromLocalStorage: (() => null) as (key: string) => null,
  getFromLocalStorage: (() => "") as (key: string) => string
});

export const LocalStorageProvider: FC<LocalStorageProviderPropertiesType> = ({
  children,
  version,
  prefix
}) => {
  const value = useMemo(
    () => ({
      addToLocalStorage: (key: string, value: string) => {
        if (isSSR()) return;
        localStorage.setItem(`${prefix}-${version}-${key}`, value);
      },
      removeFromLocalStorage: (key: string) => {
        if (isSSR()) return;
        localStorage.removeItem(`${prefix}-${version}-${key}`);
      },
      getFromLocalStorage: (key: string) => {
        if (isSSR()) return null;
        return localStorage.getItem(`${prefix}-${version}-${key}`);
      }
    }),
    [version, prefix]
  );

  return (
    <LocalStorageContext.Provider value={value}>
      {children}
    </LocalStorageContext.Provider>
  );
};

export const useLocalStorage = () => {
  const context = useContext(LocalStorageContext);
  if (context === undefined) {
    throw new Error(
      "useLocalStorage must be used within a LocalStorageProvider"
    );
  }
  return context;
};
